import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Upload, Plus, Edit, Trash2, Send, Eye, Users,
  Clock, CheckCircle, XCircle, AlertCircle, Loader2, Search,
  ChevronRight, MoreHorizontal, Download, Copy, ExternalLink, Save, RefreshCw, Folder, Pencil, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';
import ESignatureTemplateEditor from '../components/ESignatureTemplateEditor';
import DropZone from '../components/DropZone';
import PersonnelNameAutocomplete from '../components/PersonnelNameAutocomplete';
import SentDocumentsModal from '../components/SentDocumentsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: Edit },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-400', icon: AlertCircle },
};

const SIGNER_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-gray-500' },
  viewed: { label: 'Viewed', color: 'text-blue-500' },
  signed: { label: 'Signed', color: 'text-green-500' },
  declined: { label: 'Declined', color: 'text-red-500' },
};

export default function ESignatureManagement() {
  const [activeTab, setActiveTab] = useState('requests');
  const [templates, setTemplates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [runningRenewals, setRunningRenewals] = useState(false);
  const [refiling, setRefiling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkOpenedRef = useRef(false);

  // Open a request's document: signed PDF if completed, else the template PDF.
  const openRequestDocument = useCallback(async (request, download = false) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const isCompleted = request.status === 'completed';
    const endpoint = isCompleted
      ? `${API}/esignature/requests/${request.id}/completed-pdf`
      : (request.template_id ? `${API}/esignature/templates/${request.template_id}/pdf` : null);
    if (!endpoint) { toast.info('No document available to view yet.'); return; }
    try {
      const res = await axios.get(endpoint, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(request.name || 'document').replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      toast.error(isCompleted ? 'Could not load the signed document' : 'Could not open the document');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [templatesRes, requestsRes] = await Promise.all([
        axios.get(`${API}/esignature/templates`, { headers }),
        axios.get(`${API}/esignature/requests`, { headers })
      ]);
      
      setTemplates(templatesRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deep-link from the "View in app" completion email: /admin/signatures?open={id}
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || deepLinkOpenedRef.current || loading || !requests.length) return;
    const req = requests.find((r) => r.id === openId);
    if (req) {
      deepLinkOpenedRef.current = true;
      openRequestDocument(req, false);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [requests, loading, searchParams, setSearchParams, openRequestDocument]);

  const handleRunRenewals = useCallback(async () => {
    setRunningRenewals(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      // Preview first (dry run) so we can confirm before sending.
      const preview = await axios.post(`${API}/esignature/auto-renewals/run?dry_run=true`, {}, { headers });
      const wouldSend = (preview.data?.results || []).filter(r => r.status === 'would_send');
      if (wouldSend.length === 0) {
        toast.info(preview.data?.reason
          ? `No renewals to send (${preview.data.reason}).`
          : 'No credentials are due for an auto-renewal right now.');
        return;
      }
      const names = wouldSend.slice(0, 5).map(r => `${r.personnel} — ${r.credential_type}`).join('\n');
      const more = wouldSend.length > 5 ? `\n…and ${wouldSend.length - 5} more` : '';
      if (!window.confirm(`Send ${wouldSend.length} renewal request(s) now?\n\n${names}${more}`)) return;
      const res = await axios.post(`${API}/esignature/auto-renewals/run`, {}, { headers });
      toast.success(`Sent ${res.data?.sent ?? 0} renewal request(s).`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run auto-renewals');
    } finally {
      setRunningRenewals(false);
    }
  }, [fetchData]);

  const handleRefileCredentials = useCallback(async () => {
    if (!window.confirm('Re-file signed credentials for all completed documents? This files any signed TB Test, Skills Checklist, etc. onto the signer\u2019s record. Safe to run anytime — it won\u2019t create duplicates.')) return;
    setRefiling(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.post(`${API}/esignature/refile-credentials`, {}, { headers });
      toast.success(`Re-filed credentials for ${res.data?.processed ?? 0} completed document(s).`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-file credentials');
    } finally {
      setRefiling(false);
    }
  }, [fetchData]);

  // State for editing requests
  const [editingRequest, setEditingRequest] = useState(null);
  const [editingCredentialTemplate, setEditingCredentialTemplate] = useState(null);
  const [editingDefaultsTemplate, setEditingDefaultsTemplate] = useState(null);
  const [editingSignersTemplate, setEditingSignersTemplate] = useState(null);
  const [sentDocsTemplate, setSentDocsTemplate] = useState(null);

  // Delete template function
  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/esignature/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  // Move a template into a folder (prompt for a new/changed folder name).
  const moveTemplateToFolder = async (template) => {
    const existing = [...new Set(templates.map(t => t.folder).filter(Boolean))];
    const hint = existing.length ? `\nExisting: ${existing.join(', ')}` : '';
    const folder = window.prompt(`Folder for "${template.name}" (leave blank for Uncategorized):${hint}`, template.folder || '');
    if (folder === null) return; // cancelled
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API}/esignature/templates/${template.id}/folder`, { folder: folder.trim() }, { headers });
      toast.success(folder.trim() ? `Moved to "${folder.trim()}"` : 'Moved to Uncategorized');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move template');
    }
  };

  // Change a template's default expiration (days).
  const editTemplateExpiration = async (template) => {
    const val = window.prompt(`Default expiration in days for "${template.name}":`, String(template.expiration_days || 5));
    if (val === null) return;
    const days = parseInt(val, 10);
    if (!days || days < 1) { toast.error('Enter a positive number of days'); return; }
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API}/esignature/templates/${template.id}/expiration`, { expiration_days: days }, { headers });
      toast.success(`Default expiration set to ${days} day${days !== 1 ? 's' : ''}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update expiration');
    }
  };

  // If editing a template, show the editor
  if (editingTemplate) {
    return (
      <ESignatureTemplateEditor
        templateId={editingTemplate}
        onBack={() => setEditingTemplate(null)}
        onSave={() => {
          setEditingTemplate(null);
          fetchData();
        }}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Signature Workspace</h1>
          <p className="text-muted-foreground">Upload templates, place signature fields, send requests, and track completion — all in one place.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRunRenewals} disabled={runningRenewals} data-testid="run-auto-renewals-btn" title="Preview and send signature renewals for credentials expiring within 14 days">
            {runningRenewals ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Auto-Renewals
          </Button>
          <Button variant="outline" onClick={handleRefileCredentials} disabled={refiling} data-testid="refile-credentials-btn" title="Re-file signed credentials from completed documents onto each signer's record (fixes missing/expired that were already signed)">
            {refiling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />} Re-file Credentials
          </Button>
          <Button variant="outline" onClick={() => setShowUploadModal(true)} data-testid="upload-template-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload Template
          </Button>
          <Button onClick={() => setShowCreateRequestModal(true)} data-testid="new-signing-request-btn">
            <Plus className="w-4 h-4 mr-2" /> New Signing Request
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm text-blue-900" data-testid="signature-workspace-callout">
        <strong>Tip:</strong> When you upload a template, you can map it to an existing credential (e.g. <em>TB Test</em>, <em>CPR Certification</em>).
        Once any internal personnel signs it, the completed PDF is automatically filed in their Documents tab as that credential — no manual upload needed.
        Mapped credentials expiring within 14 days are also <strong>auto-sent for renewal</strong> daily (use the <em>Auto-Renewals</em> button to preview/send now).
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="requests">
            <Send className="w-4 h-4 mr-2" /> Signing Requests ({requests.length})
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="w-4 h-4 mr-2" /> Templates ({templates.length})
          </TabsTrigger>
        </TabsList>

        {/* Signing Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : requests.length === 0 ? (
            <Card className="p-12 text-center">
              <Send className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No signing requests yet</h3>
              <p className="text-muted-foreground mb-4">Create your first signing request to get documents signed</p>
              <Button onClick={() => setShowCreateRequestModal(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create Signing Request
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests
                .filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((request) => {
                  const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.draft;
                  const StatusIcon = status.icon;
                  const signedCount = request.signers.filter(s => s.status === 'signed').length;
                  
                  return (
                    <Card key={request.id} className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${status.color} flex items-center justify-center`}>
                          <StatusIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3
                              className="font-medium truncate cursor-pointer hover:underline hover:text-primary"
                              onClick={() => openRequestDocument(request)}
                              title="Open document"
                              data-testid={`open-document-${request.id}`}
                            >
                              {request.name}
                            </h3>
                            <Badge variant="secondary" className="text-xs">
                              {status.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {request.template_name} • Created {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        
                        {/* Signers Status */}
                        <div className="flex items-center gap-1">
                          {request.signers.map((signer, i) => {
                            const signerStatus = SIGNER_STATUS_CONFIG[signer.status] || SIGNER_STATUS_CONFIG.pending;
                            return (
                              <div
                                key={i}
                                className={`w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium ${signerStatus.color}`}
                                title={`${signer.name}: ${signerStatus.label}`}
                              >
                                {signer.name.charAt(0).toUpperCase()}
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="text-sm text-muted-foreground">
                          {signedCount}/{request.signers.length} signed
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <SigningRequestActions request={request} onUpdate={fetchData} onEdit={setEditingRequest} />
                        </div>
                      </div>
                    </Card>
                  );
                })}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : templates.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No templates yet</h3>
              <p className="text-muted-foreground mb-4">Upload a PDF to create your first document template</p>
              <Button onClick={() => setShowUploadModal(true)}>
                <Upload className="w-4 h-4 mr-2" /> Upload Template
              </Button>
            </Card>
          ) : (
            (() => {
              const groups = {};
              templates.forEach(t => { const k = t.folder || 'Uncategorized'; (groups[k] = groups[k] || []).push(t); });
              const names = Object.keys(groups).sort((a, b) => a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b));
              return names.map(folderName => (
                <div key={folderName} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold" data-testid={`template-folder-header-${folderName}`}>{folderName}</h3>
                    <Badge variant="secondary" className="text-xs">{groups[folderName].length}</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groups[folderName].map((template) => (
                <Card key={template.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{template.name}</h3>
                      <p className="text-sm text-muted-foreground truncate">
                        {template.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                    <span>{template.page_count} page{template.page_count !== 1 ? 's' : ''}</span>
                    <span>{template.fields?.length || 0} fields</span>
                  </div>
                  
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Signers:</span>
                    {template.signer_roles?.map((role, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{role}</Badge>
                    ))}
                  </div>

                  {template.credential_type && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`template-credential-badge-${template.id}`}>
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                        Maps to credential: {template.credential_type}
                        {template.credential_expiration_months ? ` · ${template.credential_expiration_months}mo` : ''}
                      </Badge>
                    </div>
                  )}
                  
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                    <button type="button" onClick={() => moveTemplateToFolder(template)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-muted" data-testid={`template-move-folder-${template.id}`}>
                      <Folder className="w-3 h-3" /> {template.folder || 'Uncategorized'}
                    </button>
                    <button type="button" onClick={() => editTemplateExpiration(template)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-muted" data-testid={`template-expiration-${template.id}`}>
                      <Clock className="w-3 h-3" /> Expires {template.expiration_days || 5}d
                    </button>
                    <button type="button" onClick={() => setEditingCredentialTemplate(template)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-muted ${template.credential_type ? 'border-blue-300 text-blue-700' : ''}`} data-testid={`template-credential-${template.id}`}>
                      <FileText className="w-3 h-3" /> {template.credential_type ? `Credential: ${template.credential_type}` : 'Link credential'}
                    </button>
                    <button type="button" onClick={() => setEditingDefaultsTemplate(template)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-muted ${template.default_request_name || template.default_message ? 'border-emerald-300 text-emerald-700' : ''}`} data-testid={`template-defaults-${template.id}`}>
                      <Pencil className="w-3 h-3" /> Defaults
                    </button>
                    <button type="button" onClick={() => setEditingSignersTemplate(template)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-muted ${template.role_presets?.length ? 'border-indigo-300 text-indigo-700' : ''}`} data-testid={`template-signers-${template.id}`}>
                      <Users className="w-3 h-3" /> {template.role_presets?.length ? 'Signers set' : 'Signers'}
                    </button>
                    <button type="button" onClick={() => setSentDocsTemplate(template)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-muted" data-testid={`template-sent-docs-${template.id}`}>
                      <Send className="w-3 h-3" /> Sent Documents
                    </button>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => setEditingTemplate(template.id)}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit Fields
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowCreateRequestModal(true);
                      }}
                    >
                      <Send className="w-4 h-4 mr-1" /> Send
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => deleteTemplate(template.id, template.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
                    ))}
                  </div>
                </div>
              ));
            })()
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Template Modal */}
      {showUploadModal && (
        <UploadTemplateModal 
          onClose={() => setShowUploadModal(false)} 
          onSuccess={() => {
            setShowUploadModal(false);
            fetchData();
          }}
        />
      )}

      {/* Create Signing Request Modal */}
      {showCreateRequestModal && (
        <CreateSigningRequestModal
          templates={templates}
          selectedTemplate={selectedTemplate}
          onClose={() => {
            setShowCreateRequestModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={() => {
            setShowCreateRequestModal(false);
            setSelectedTemplate(null);
            fetchData();
          }}
        />
      )}

      {/* Edit Signing Request Modal */}
      {editingRequest && (
        <EditSigningRequestModal
          request={editingRequest}
          onClose={() => setEditingRequest(null)}
          onSuccess={() => {
            setEditingRequest(null);
            fetchData();
          }}
        />
      )}

      {/* Edit Template Credential Link Modal */}
      {editingCredentialTemplate && (
        <EditCredentialModal
          template={editingCredentialTemplate}
          onClose={() => setEditingCredentialTemplate(null)}
          onSuccess={() => {
            setEditingCredentialTemplate(null);
            fetchData();
          }}
        />
      )}
      {/* Edit Template Defaults Modal */}
      {editingDefaultsTemplate && (
        <EditTemplateDefaultsModal
          template={editingDefaultsTemplate}
          onClose={() => setEditingDefaultsTemplate(null)}
          onSuccess={() => {
            setEditingDefaultsTemplate(null);
            fetchData();
          }}
        />
      )}
      {/* Edit Template Signers (role presets) Modal */}
      {editingSignersTemplate && (
        <EditSignersModal
          template={editingSignersTemplate}
          onClose={() => setEditingSignersTemplate(null)}
          onSuccess={() => {
            setEditingSignersTemplate(null);
            fetchData();
          }}
        />
      )}

      {/* Sent Documents (per-template) Modal */}
      {sentDocsTemplate && (
        <SentDocumentsModal
          template={sentDocsTemplate}
          onClose={() => setSentDocsTemplate(null)}
        />
      )}
    </div>
  );
}

// Edit Template → Preset signers per role (Signer=new hire, Supervisor=manager, EHR Rep=specific staff)
function EditSignersModal({ template, onClose, onSuccess }) {
  const roles = template.signer_roles?.length ? template.signer_roles : ['Signer'];
  const guessResolution = (role) => {
    const r = (role || '').toLowerCase();
    if (r.includes('supervisor') || r.includes('manager')) return 'supervisor';
    if (r === 'signer' || r.includes('new hire') || r.includes('employee')) return 'new_hire';
    return 'user';
  };
  const [presets, setPresets] = useState(
    roles.map((role) => {
      const existing = (template.role_presets || []).find((p) => p.role === role);
      return existing || { role, resolution: guessResolution(role), user_id: null, user_name: '', email: '', name: '' };
    })
  );
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
  }, []);

  // Backfill display name/email for legacy presets saved with only a user_id.
  useEffect(() => {
    if (!staff.length) return;
    setPresets((prev) => prev.map((p) => {
      if (p.resolution === 'user' && p.user_id && (!p.user_name || !p.email)) {
        const u = staff.find((s) => s.id === p.user_id);
        if (u) return { ...p, user_name: p.user_name || u.name || u.email, email: p.email || u.email || '' };
      }
      return p;
    }));
  }, [staff]);

  const update = (idx, patch) => setPresets((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = presets.map((p) => {
        if (p.resolution === 'user') {
          return { role: p.role, resolution: 'user', user_id: p.user_id || null, user_name: p.user_name || null, email: p.email || null };
        }
        if (p.resolution === 'email') {
          return { role: p.role, resolution: 'email', email: p.email || null, name: p.name || null };
        }
        return { role: p.role, resolution: p.resolution };
      });
      await axios.put(`${API}/esignature/templates/${template.id}/role-presets`, { role_presets: payload }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Signers saved');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save signers');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4 p-6" data-testid="edit-signers-modal">
        <h2 className="text-xl font-semibold mb-1">Preset Signers</h2>
        <p className="text-sm text-muted-foreground mb-4">
          For "{template.name}" — set who fills each role when this document is sent during onboarding.
          {template.signing_order_type === 'sequential' ? ' Signing order: sequential (top to bottom).' : ' Signing order: parallel (all at once).'}
        </p>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {presets.map((p, idx) => (
            <div key={idx} className="border rounded-md p-3" data-testid={`signer-role-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">{template.signing_order_type === 'sequential' ? `${idx + 1}. ` : ''}{p.role}</span>
              </div>
              <select
                value={p.resolution}
                onChange={(e) => update(idx, { resolution: e.target.value })}
                className="w-full h-9 px-3 text-sm border rounded-md bg-background"
                data-testid={`signer-resolution-${idx}`}
              >
                <option value="new_hire">New hire (document files into their record)</option>
                <option value="supervisor">Supervisor (the new hire's manager)</option>
                <option value="user">Specific person (search by name)</option>
                <option value="email">External email</option>
              </select>
              {p.resolution === 'user' && (
                <div className="mt-2 space-y-2">
                  <PersonnelNameAutocomplete
                    value={p.user_name || ''}
                    onChange={(v) => update(idx, { user_name: v, user_id: null })}
                    onSelect={(person) => update(idx, { user_name: person.name, email: person.email, user_id: null })}
                    placeholder="Type a name, e.g. Jarrad"
                    testId={`signer-person-${idx}`}
                  />
                  <Input
                    type="email"
                    placeholder="Email to send the signing link to"
                    value={p.email || ''}
                    onChange={(e) => update(idx, { email: e.target.value })}
                    data-testid={`signer-person-email-${idx}`}
                  />
                  <p className="text-xs text-muted-foreground">
                    The <strong>name</strong> is used on the signature; the <strong>email</strong> is where the signing link is sent. Adjust the email if it differs from the person's record.
                  </p>
                  {p.user_name && !p.email && (
                    <p className="text-xs text-amber-600">Add an email so this person can receive the signing link.</p>
                  )}
                </div>
              )}
              {p.resolution === 'email' && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input placeholder="Name" value={p.name || ''} onChange={(e) => update(idx, { name: e.target.value })} data-testid={`signer-ext-name-${idx}`} />
                  <Input placeholder="email@example.com" value={p.email || ''} onChange={(e) => update(idx, { email: e.target.value })} data-testid={`signer-ext-email-${idx}`} />
                </div>
              )}
              {p.resolution === 'supervisor' && (
                <p className="text-xs text-muted-foreground mt-2">Uses the new hire's Manager. If none is set, defaults to Doug (doug@elitehomerehab.com).</p>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-4">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="button" className="flex-1" onClick={handleSave} disabled={saving} data-testid="save-signers-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </Card>
    </div>
  );
}

// Edit Template → Request defaults (name / message / signing order)
function EditTemplateDefaultsModal({ template, onClose, onSuccess }) {
  const [requestName, setRequestName] = useState(template.default_request_name || '');
  const [message, setMessage] = useState(template.default_message || '');
  const [signingOrderType, setSigningOrderType] = useState(template.signing_order_type || 'parallel');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/esignature/templates/${template.id}/defaults`, {
        default_request_name: requestName.trim() || null,
        default_message: message.trim() || null,
        signing_order_type: signingOrderType,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Template defaults saved');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md mx-4 p-6" data-testid="edit-template-defaults-modal">
        <h2 className="text-xl font-semibold mb-1">Request Defaults</h2>
        <p className="text-sm text-muted-foreground mb-4">For "{template.name}" — these pre-fill the Create Signing Request form (still editable per request).</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Default Request Name</label>
            <Input
              value={requestName}
              onChange={(e) => setRequestName(e.target.value)}
              placeholder="e.g., PT Skills Checklist"
              data-testid="defaults-request-name"
            />
            <p className="text-xs text-muted-foreground mt-1">The signer's first initial + last name is appended automatically when sending (e.g. "…- S. Alaway"). Blank = use the template name.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Default Message to Signers</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Pre-filled message for every request"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
              data-testid="defaults-message"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Default Signing Order</label>
            <select
              value={signingOrderType}
              onChange={(e) => setSigningOrderType(e.target.value)}
              className="w-full h-9 px-3 text-sm border rounded-md bg-background"
              data-testid="defaults-signing-order"
            >
              <option value="parallel">Parallel (all at once)</option>
              <option value="sequential">Sequential (in order)</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="button" className="flex-1" onClick={handleSave} disabled={saving} data-testid="save-template-defaults">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Edit Template → Credential Link Modal
function EditCredentialModal({ template, onClose, onSuccess }) {
  const [credentialType, setCredentialType] = useState(template.credential_type || '');
  const [months, setMonths] = useState(template.credential_expiration_months ? String(template.credential_expiration_months) : '');
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/credentials/types`)
      .then(res => setCredentialTypes(res.data?.types || res.data || []))
      .catch(() => setCredentialTypes([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API}/esignature/templates/${template.id}/credential`, {
        credential_type: credentialType || null,
        credential_expiration_months: credentialType && months ? Number(months) : null,
      }, { headers });
      toast.success(credentialType ? `Linked to "${credentialType}"` : 'Credential link removed');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credential link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="p-6 w-full max-w-md" data-testid="edit-credential-modal">
        <h3 className="text-lg font-semibold mb-1">Link to Credential</h3>
        <p className="text-sm text-muted-foreground mb-4">
          When a signer who is internal staff completes "{template.name}", the signed PDF is auto-filed as this credential.
        </p>
        <label className="block text-sm font-medium mb-1">Credential type</label>
        <select
          value={credentialType}
          onChange={(e) => setCredentialType(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm mb-3"
          data-testid="credential-type-select"
        >
          <option value="">— None (don't create a credential) —</option>
          {credentialTypes.map((t) => {
            const val = typeof t === 'string' ? t : (t.name || t.type || t.value);
            return <option key={val} value={val}>{val}</option>;
          })}
        </select>
        {credentialType && (
          <>
            <label className="block text-sm font-medium mb-1">Expires after (months, optional)</label>
            <Input
              type="number"
              min="1"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              placeholder="e.g., 12"
              className="mb-3"
              data-testid="credential-months-input"
            />
          </>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={save} disabled={saving} className="flex-1" data-testid="credential-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </Card>
    </div>
  );
}

// Upload Template Modal Component
function UploadTemplateModal({ onClose, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [signerRoles, setSignerRoles] = useState('Signer');
  const [credentialType, setCredentialType] = useState('');
  const [credentialMonths, setCredentialMonths] = useState('');
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [folder, setFolder] = useState('');
  const [expirationDays, setExpirationDays] = useState('5');
  const [defaultRequestName, setDefaultRequestName] = useState('');
  const [defaultMessage, setDefaultMessage] = useState('Hello! Please review and sign to complete. Thanks!');
  const [signingOrderType, setSigningOrderType] = useState('parallel');
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    axios.get(`${API}/esignature/templates/folders/list`)
      .then(res => setFolders(res.data?.folders || []))
      .catch(() => setFolders([]));
  }, []);

  useEffect(() => {
    // Pull live list of credential types so we don't hardcode
    axios.get(`${API}/credentials/types`)
      .then(res => setCredentialTypes(res.data?.types || []))
      .catch(() => setCredentialTypes([
        'TB Test', 'CPR Certification', 'Skills Checklist', 'Professional Liability Insurance', 'Other'
      ]));
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name || file.name.replace('.pdf', ''));
      formData.append('description', description);
      formData.append('signer_roles', signerRoles);
      if (credentialType) formData.append('credential_type', credentialType);
      if (credentialMonths) formData.append('credential_expiration_months', credentialMonths);
      if (folder.trim()) formData.append('folder', folder.trim());
      formData.append('expiration_days', expirationDays || '5');
      if (defaultRequestName.trim()) formData.append('default_request_name', defaultRequestName.trim());
      if (defaultMessage.trim()) formData.append('default_message', defaultMessage.trim());
      formData.append('signing_order_type', signingOrderType);

      await axios.post(`${API}/esignature/templates`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Template uploaded successfully');
      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold mb-4">Upload Document Template</h2>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">PDF File *</label>
            <DropZone
              file={file}
              onFile={(f) => setFile(f)}
              onClear={() => setFile(null)}
              accept="application/pdf,.pdf"
              acceptExtensions={['pdf']}
              label="Drag and drop a PDF here"
              hint="PDF only"
              testId="esign-template-upload"
              compact
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Template Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Employment Agreement"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Signer Roles (comma-separated)</label>
            <Input
              value={signerRoles}
              onChange={(e) => setSignerRoles(e.target.value)}
              placeholder="e.g., Employee, Manager, HR"
            />
            <p className="text-xs text-muted-foreground mt-1">Define roles for multiple signers</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Default Request Name</label>
            <Input
              value={defaultRequestName}
              onChange={(e) => setDefaultRequestName(e.target.value)}
              placeholder="e.g., PT Skills Checklist"
              data-testid="template-default-request-name"
            />
            <p className="text-xs text-muted-foreground mt-1">The signer's first initial + last name is appended automatically when sending (e.g. "…- S. Alaway"). Leave blank to use the template name.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Default Message to Signers</label>
            <textarea
              value={defaultMessage}
              onChange={(e) => setDefaultMessage(e.target.value)}
              placeholder="Pre-filled message for every request (editable per request)"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px]"
              data-testid="template-default-message"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Default Signing Order</label>
            <select
              value={signingOrderType}
              onChange={(e) => setSigningOrderType(e.target.value)}
              className="w-full h-9 px-3 text-sm border rounded-md bg-background"
              data-testid="template-default-signing-order"
            >
              <option value="parallel">Parallel (all at once)</option>
              <option value="sequential">Sequential (in order)</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Folder</label>
              <Input
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                placeholder="e.g., Offer Letters"
                list="esign-folder-options"
                data-testid="template-folder-input"
              />
              <datalist id="esign-folder-options">
                {folders.map(f => <option key={f} value={f} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Expires after (days)</label>
              <Input
                type="number"
                min="1"
                max="365"
                value={expirationDays}
                onChange={(e) => setExpirationDays(e.target.value)}
                placeholder="5"
                data-testid="template-expiration-days"
              />
              <p className="text-xs text-muted-foreground mt-1">Default for requests from this template.</p>
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Credential mapping (optional)</label>
              <p className="text-xs text-muted-foreground mb-2">
                When set, every internal signer who signs this document gets the completed PDF
                automatically saved as this credential on their Documents tab.
              </p>
              <select
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value)}
                className="w-full h-9 px-3 text-sm border rounded-md bg-background"
                data-testid="template-credential-type"
              >
                <option value="">— Not a credential —</option>
                {credentialTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            {credentialType && (
              <div>
                <label className="block text-xs font-medium mb-1">Valid for (months)</label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  value={credentialMonths}
                  onChange={(e) => setCredentialMonths(e.target.value)}
                  placeholder="e.g., 12 → expires 1 year after signing"
                  data-testid="template-credential-months"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank for credentials that don't expire (e.g., one-time attestations).
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={uploading || !file}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// Create Signing Request Modal Component
function CreateSigningRequestModal({ templates, selectedTemplate, onClose, onSuccess }) {
  const [creating, setCreating] = useState(false);
  const [templateId, setTemplateId] = useState(selectedTemplate?.id || '');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [signingOrderType, setSigningOrderType] = useState('parallel');
  const [signers, setSigners] = useState([{ type: 'external', name: '', email: '', role: 'Signer', order: 0 }]);
  const [fanOut, setFanOut] = useState(false);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
  }, []);

  // Backfill emails for fixed "specific staff" signers once the staff list loads.
  useEffect(() => {
    if (!staff.length) return;
    setSigners((prev) => prev.map((s) => (s.fixed && s.user_id && !s.email)
      ? { ...s, email: staff.find((u) => u.id === s.user_id)?.email || s.email }
      : s));
  }, [staff]);
  const [expiresDays, setExpiresDays] = useState(5);
  const [fromChoice, setFromChoice] = useState('admin');
  const [nameTouched, setNameTouched] = useState(false);

  const template = templates.find(t => t.id === templateId);

  // Build "F. Lastname" suffix from the primary signer's full name.
  const buildSignerSuffix = (fullName) => {
    const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts[0][0].toUpperCase()}. ${parts[parts.length - 1]}`;
  };

  // Auto-build the request name from the template preset + the first signer's name,
  // until the user manually edits the Request Name field.
  useEffect(() => {
    if (!template || nameTouched) return;
    const base = (template.default_request_name || '').trim() || template.name || '';
    const suffix = buildSignerSuffix(signers[0]?.name);
    setName(suffix ? `${base} - ${suffix}` : base);
  }, [template, signers, nameTouched]);

  useEffect(() => {
    if (template) {
      setExpiresDays(template.expiration_days || 5);
    }
  }, [template]);

  useEffect(() => {
    if (template) {
      // Initialize signers based on template roles, pre-filling any role that has a
      // fixed preset — a "Specific staff member" (resolution 'user') or an
      // "External email" (resolution 'email'). Dynamic roles (new_hire/supervisor)
      // stay blank for manual sends.
      const presets = template.role_presets || [];
      const presetFor = (role) => presets.find(
        (p) => p.role === role && ['user', 'email', 'specific'].includes(p.resolution)
      );
      const initialSigners = template.signer_roles.map((role, index) => {
        const pr = presetFor(role);
        if (!pr) {
          return { type: 'external', name: '', email: '', fixed: false, role, order: index };
        }
        const isUser = pr.resolution === 'user' || pr.resolution === 'specific';
        return {
          type: isUser ? 'internal' : 'external',
          name: isUser ? (pr.user_name || pr.name || '') : (pr.name || ''),
          email: isUser ? (pr.email || staff.find((u) => u.id === pr.user_id)?.email || '') : (pr.email || ''),
          user_id: isUser ? (pr.user_id || undefined) : undefined,
          fixed: true,
          role,
          order: index,
        };
      });
      setSigners(initialSigners.length > 0 ? initialSigners : [{ type: 'external', name: '', email: '', role: 'Signer', order: 0 }]);
      // Use template's signing order type as default
      setSigningOrderType(template.signing_order_type || 'parallel');
      // Default to fan-out (one private document per recipient) whenever that's
      // the natural intent: (a) a SINGLE-ROLE template — e.g. an Annual TB
      // Questionnaire sent to many therapists, each signs their own copy — or
      // (b) a template with a fixed countersigner (e.g. EHR Rep) that each
      // recipient's own copy routes to. Admins can still uncheck it to put
      // everyone on one shared document.
      const singleRole = (template.signer_roles?.length || 1) <= 1;
      const hasFixedCounter = initialSigners.some((s) => s.fixed) && initialSigners.length >= 2;
      setFanOut(singleRole || hasFixedCounter);
      // Pre-fill the message from the template default (editable per request)
      setMessage(template.default_message || '');
      // Allow the request name to auto-rebuild for the newly selected template
      setNameTouched(false);
    }
  }, [template]);

  const updateSigner = (index, field, value) => {
    const newSigners = [...signers];
    newSigners[index] = { ...newSigners[index], [field]: value };
    setSigners(newSigners);
  };

  const addSigner = () => {
    const primaryRole = template?.signer_roles?.[0] || 'Signer';
    setSigners([...signers, {
      type: 'external',
      name: '',
      email: '',
      // When fanning out, extra rows are additional PRIMARY-role recipients (each
      // gets their own private document); otherwise use the next role in order.
      role: fanOut ? primaryRole : (template?.signer_roles?.[signers.length] || 'Signer'),
      order: signers.length
    }]);
  };

  const removeSigner = (index) => {
    if (signers.length > 1) {
      setSigners(signers.filter((_, i) => i !== index));
    }
  };

  // Move signer up/down in order
  const moveSigner = (index, direction) => {
    const newSigners = [...signers];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= signers.length) return;
    
    [newSigners[index], newSigners[newIndex]] = [newSigners[newIndex], newSigners[index]];
    // Update order values
    newSigners.forEach((s, i) => s.order = i);
    setSigners(newSigners);
  };

  const handleCreate = async (e, sendNow = false) => {
    if (e?.preventDefault) e.preventDefault();
    
    // Validate
    if (!templateId) {
      toast.error('Please select a template');
      return;
    }
    
    const validSigners = signers.filter(s => s.name && s.email);
    if (validSigners.length === 0) {
      toast.error('Please add at least one signer with name and email');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const roles = template.signer_roles && template.signer_roles.length ? template.signer_roles : ['Signer'];
      const primaryRole = roles[0];
      const baseName = (template.default_request_name || '').trim() || template.name || 'Document';

      if (fanOut) {
        // FAN-OUT: send a SEPARATE, private document to each primary-role recipient,
        // each countersigned by the same fixed signer(s) (e.g., EHR Rep). Recipients
        // never see each other. Uses the template's signing order.
        const primaries = validSigners.filter(s => s.role === primaryRole);
        const counters = validSigners.filter(s => s.role !== primaryRole);
        if (primaries.length === 0) {
          toast.error(`Add at least one ${primaryRole} recipient`);
          setCreating(false);
          return;
        }
        let ok = 0;
        const failedNames = [];
        for (const p of primaries) {
          try {
            const reqSigners = [
              { type: p.type || (p.user_id ? 'internal' : 'external'), name: p.name, email: p.email, user_id: p.user_id, personnel_id: p.personnel_id, role: primaryRole, order: 0 },
              ...counters.map((c, i) => ({ type: c.type || (c.user_id ? 'internal' : (c.personnel_id ? 'internal' : 'external')), name: c.name, email: c.email, user_id: c.user_id, personnel_id: c.personnel_id, role: c.role, order: i + 1 })),
            ];
            const suffix = buildSignerSuffix(p.name);
            const resp = await axios.post(`${API}/esignature/requests`, {
              template_id: templateId,
              name: suffix ? `${baseName} - ${suffix}` : baseName,
              message,
              signers: reqSigners,
              signing_order_type: signingOrderType,
              expires_days: Number(expiresDays) || 5,
            }, { headers });
            const newId = resp.data?.signing_request?.id || resp.data?.id;
            if (sendNow && newId) {
              await axios.post(`${API}/esignature/requests/${newId}/send`, { from_choice: fromChoice }, { headers });
            }
            ok += 1;
          } catch (err) {
            console.error('Fan-out send failed for', p.name, err);
            failedNames.push(p.name);
          }
        }
        if (ok > 0) {
          toast.success(sendNow
            ? `Sent ${ok} separate document${ok !== 1 ? 's' : ''} — each recipient signs their own, then it routes to you`
            : `Created ${ok} draft document${ok !== 1 ? 's' : ''}`);
        }
        if (failedNames.length > 0) {
          toast.error(`Failed for: ${failedNames.join(', ')}`);
        }
        onSuccess();
        return;
      }

      const response = await axios.post(`${API}/esignature/requests`, {
        template_id: templateId,
        name: name || `${template.name} - ${new Date().toLocaleDateString()}`,
        message,
        signers: validSigners,
        signing_order_type: signingOrderType,
        expires_days: Number(expiresDays) || 5
      }, { headers });

      const newId = response.data?.signing_request?.id || response.data?.id;

      if (sendNow && newId) {
        await axios.post(`${API}/esignature/requests/${newId}/send`,
          { from_choice: fromChoice },
          { headers });
        toast.success('Signing request created and sent');
      } else {
        toast.success('Signing request saved as draft');
      }
      onSuccess();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create signing request');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-auto py-8">
      <Card className="w-full max-w-2xl mx-4 p-6">
        <h2 className="text-xl font-semibold mb-4">Create Signing Request</h2>
        
        <form onSubmit={(e) => handleCreate(e, true)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Select Template *</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            >
              <option value="">Select a template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Request Name</label>
            <Input
              value={name}
              onChange={(e) => { setNameTouched(true); setName(e.target.value); }}
              placeholder="e.g., John Smith Employment Agreement"
              data-testid="create-request-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Message to Signers</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional message to include in the signing request email"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
              data-testid="create-request-message"
            />
          </div>

          {/* Signing Order */}
          <div>
            <label className="block text-sm font-medium mb-2">Signing Order</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="signingOrder"
                  checked={signingOrderType === 'parallel'}
                  onChange={() => setSigningOrderType('parallel')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Parallel (all at once)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="signingOrder"
                  checked={signingOrderType === 'sequential'}
                  onChange={() => setSigningOrderType('sequential')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Sequential (in order)</span>
              </label>
            </div>
            {signingOrderType === 'sequential' && (
              <p className="text-xs text-muted-foreground mt-1">
                Signers must sign in the order listed below. Use arrows to reorder.
              </p>
            )}
          </div>

          {/* Fan-out toggle */}
          {template && template.signer_roles?.length >= 1 && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fanOut}
                  onChange={(e) => setFanOut(e.target.checked)}
                  className="w-4 h-4 mt-0.5"
                  data-testid="fanout-toggle"
                />
                <span className="text-sm">
                  <span className="font-medium">Send individually to each {template.signer_roles[0]}</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Creates a separate, private document per {template.signer_roles[0]} recipient. Each one signs their own copy, then it routes to the other signer(s){template.signer_roles.length > 1 ? ` (e.g., ${template.signer_roles.slice(1).join(', ')})` : ''}. Recipients never see each other.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Signers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">{fanOut ? `${template?.signer_roles?.[0] || 'Signer'} recipients & countersigners` : 'Signers'}</label>
              <Button type="button" variant="outline" size="sm" onClick={addSigner}>
                <Plus className="w-4 h-4 mr-1" /> {fanOut ? `Add ${template?.signer_roles?.[0] || 'recipient'}` : 'Add Signer'}
              </Button>
            </div>
            
            <div className="space-y-3">
              {signers.map((signer, index) => (
                <div key={index} className="p-3 border rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {signingOrderType === 'sequential' && (
                        <div className="flex flex-col">
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                            onClick={() => moveSigner(index, 'up')}
                            disabled={index === 0}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                            onClick={() => moveSigner(index, 'down')}
                            disabled={index === signers.length - 1}
                          >
                            ▼
                          </button>
                        </div>
                      )}
                      <span className="text-sm font-medium">
                        {signingOrderType === 'sequential' ? `${index + 1}. ` : ''}{signer.role || `Signer ${index + 1}`}
                        {signer.fixed && <span className="ml-1 text-xs text-indigo-600">(from template)</span>}
                      </span>
                    </div>
                    {signers.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSigner(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <PersonnelNameAutocomplete
                      value={signer.name}
                      onChange={(v) => updateSigner(index, 'name', v)}
                      onSelect={(p) => {
                        const newSigners = [...signers];
                        newSigners[index] = {
                          ...newSigners[index],
                          name: p.name,
                          email: p.email || newSigners[index].email,
                          type: 'internal',
                          personnel_id: p.id,
                        };
                        setSigners(newSigners);
                      }}
                      placeholder="Name *"
                      required
                      testId={`create-signer-name-${index}`}
                    />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={signer.email}
                      onChange={(e) => updateSigner(index, 'email', e.target.value)}
                      required
                    />
                    <Input
                      placeholder="Role"
                      value={signer.role}
                      onChange={(e) => updateSigner(index, 'role', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expiration + Send-from */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Expires after (days)</label>
              <Input
                type="number"
                min="1"
                max="365"
                value={expiresDays}
                onChange={(e) => setExpiresDays(e.target.value)}
                data-testid="request-expires-days"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Defaults from the template{template ? ` (${template.expiration_days || 5}d)` : ''} — change for this request only.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Send from</label>
              <select
                value={fromChoice}
                onChange={(e) => setFromChoice(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="request-from-choice"
              >
                <option value="admin">Shared admin inbox</option>
                <option value="me">My email</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Used only when you Create &amp; Send.</p>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} data-testid="request-cancel-btn">
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              disabled={creating}
              onClick={(e) => handleCreate(e, false)}
              data-testid="request-save-draft-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save as Draft
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={creating}
              data-testid="request-create-send-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Create &amp; Send
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// Signing Request Actions Component
function SigningRequestActions({ request, onUpdate, onEdit }) {
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null); // 'send' | 'resend' | null

  // Read current user once to display "send as me" address
  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; }
  }, []);
  const myEmail = currentUser?.email || '';

  const doSend = async (fromChoice) => {
    setPickerOpen(null);
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/esignature/requests/${request.id}/send`,
        { from_choice: fromChoice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sent_count, failed = [], reply_to } = response.data;
      if (sent_count > 0 && failed.length === 0) {
        toast.success(`Sent to ${sent_count} signer${sent_count === 1 ? '' : 's'}${reply_to ? ` (replies → ${reply_to})` : ''}`);
      } else if (sent_count > 0) {
        toast.warning(`Sent to ${sent_count}, failed to: ${failed.join(', ')}`);
      } else {
        toast.error('No emails could be sent. Check email settings.');
      }
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  const doResend = async (fromChoice) => {
    setPickerOpen(null);
    setResending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/esignature/requests/${request.id}/resend`,
        { from_choice: fromChoice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sent_count, failed = [] } = response.data;
      if (sent_count > 0 && failed.length === 0) {
        toast.success(`Resent to ${sent_count} pending signer${sent_count === 1 ? '' : 's'}`);
      } else if (sent_count > 0) {
        toast.warning(`Resent to ${sent_count}, failed to: ${failed.join(', ')}`);
      } else {
        toast.error('No emails could be sent. Check email settings.');
      }
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend request');
    } finally {
      setResending(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this signing request? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/esignature/requests/${request.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Signing request cancelled');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleDelete = async () => {
    const inProgress = ['sent', 'in_progress'].includes(request.status);
    const warning = inProgress
      ? `Permanently delete "${request.name}"? This is still in progress — signers will lose access to their signing link. This cannot be undone.`
      : `Permanently delete "${request.name}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/esignature/requests/${request.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Signing request deleted');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  const handleViewSigned = async (download = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/esignature/requests/${request.id}/completed-pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(request.name || 'signed-document').replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the signed document');
    }
  };

  const handleEditExpiration = async () => {
    const current = request.expires_days || '';
    const val = window.prompt(`Set this request to expire in how many days from now?${current ? ` (currently set to ${current})` : ''}`, String(current || 14));
    if (val === null) return;
    const days = parseInt(val, 10);
    if (!days || days < 1) { toast.error('Enter a positive number of days'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/esignature/requests/${request.id}/expiration`,
        { expires_days: days },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Expiration updated — now expires in ${days} day${days !== 1 ? 's' : ''}`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expiration');
    }
  };

  const copySigningLink = (signer) => {
    const url = `${window.location.origin}/sign/${request.id}?token=${signer.signing_token}`;
    navigator.clipboard.writeText(url);
    toast.success(`Signing link copied for ${signer.name}`);
  };

  const copyAllLinks = () => {
    const links = request.signers.map(s => 
      `${s.name}: ${window.location.origin}/sign/${request.id}?token=${s.signing_token}`
    ).join('\n');
    navigator.clipboard.writeText(links);
    toast.success('All signing links copied to clipboard');
  };

  return (
    <div className="flex items-center gap-1 relative">
      {request.status === 'draft' && (
        <>
          <Button variant="outline" size="sm" onClick={() => onEdit(request)} title="Edit request">
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => setPickerOpen(pickerOpen === 'send' ? null : 'send')}
            disabled={sending}
            title="Send signing request"
            data-testid="send-signing-request"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
          {pickerOpen === 'send' && (
            <SendFromPicker myEmail={myEmail} onPick={doSend} onClose={() => setPickerOpen(null)} />
          )}
          <Button variant="outline" size="sm" onClick={handleEditExpiration} title="Set expiration" data-testid="edit-expiration-btn">
            <Clock className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            title="Delete request"
            className="text-red-600 hover:text-red-700"
            data-testid="delete-signing-request"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      )}
      
      {['sent', 'in_progress'].includes(request.status) && (
        <>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onEdit(request)}
            title="Edit signers"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={copyAllLinks}
            title="Copy all signing links"
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPickerOpen(pickerOpen === 'resend' ? null : 'resend')}
            disabled={resending}
            title="Resend to pending signers"
            data-testid="resend-signing-request"
          >
            {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
          {pickerOpen === 'resend' && (
            <SendFromPicker myEmail={myEmail} onPick={doResend} onClose={() => setPickerOpen(null)} />
          )}
          <Button variant="outline" size="sm" onClick={handleEditExpiration} title="Extend / change expiration" data-testid="edit-expiration-btn">
            <Clock className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleCancel} title="Cancel request (keeps record)">
            <XCircle className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            title="Delete request entirely"
            className="text-red-600 hover:text-red-700"
            data-testid="delete-signing-request"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      )}
      
      {request.status === 'completed' && (
        <>
          <Button variant="outline" size="sm" onClick={() => handleViewSigned(false)} title="View signed document" data-testid="view-signed-document">
            <Eye className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleViewSigned(true)} title="Download signed document" data-testid="download-signed-document">
            <Download className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            title="Delete request"
            className="text-red-600 hover:text-red-700"
            data-testid="delete-signing-request"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      )}
      
      {request.status === 'cancelled' && (
        <>
          <Badge variant="secondary" className="text-xs">Cancelled</Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            title="Delete request"
            className="text-red-600 hover:text-red-700"
            data-testid="delete-signing-request"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      )}
    </div>
  );
}

// Small popover that lets the user pick which "From" identity to send as.
function SendFromPicker({ myEmail, onPick, onClose }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1 z-50 min-w-[260px] bg-white border rounded-md shadow-lg py-1"
      data-testid="send-from-picker"
    >
      <button
        type="button"
        onClick={() => onPick('admin')}
        className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
        data-testid="send-from-admin"
      >
        <div className="font-medium">Send from Admin (default)</div>
        <div className="text-xs text-muted-foreground">Replies go to the company HR inbox</div>
      </button>
      <button
        type="button"
        onClick={() => onPick('me')}
        disabled={!myEmail}
        className="w-full text-left px-3 py-2 hover:bg-muted text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="send-from-me"
      >
        <div className="font-medium">Send from me{myEmail ? '' : ' (your account email missing)'}</div>
        <div className="text-xs text-muted-foreground">
          {myEmail ? `Replies route to ${myEmail}` : 'Add an email to your user profile to enable this'}
        </div>
      </button>
    </div>
  );
}

// Edit Signing Request Modal Component
function EditSigningRequestModal({ request, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(request.name);
  const [message, setMessage] = useState(request.message || '');
  const [signers, setSigners] = useState(request.signers.map(s => ({
    id: s.id,
    type: s.type || 'external',
    name: s.name,
    email: s.email,
    role: s.role,
    order: s.order,
    status: s.status
  })));

  const updateSigner = (index, field, value) => {
    const newSigners = [...signers];
    newSigners[index] = { ...newSigners[index], [field]: value };
    setSigners(newSigners);
  };

  const addSigner = () => {
    setSigners([...signers, { 
      id: `new_${Date.now()}`,
      type: 'external', 
      name: '', 
      email: '', 
      role: 'Signer', 
      order: signers.length,
      status: 'pending'
    }]);
  };

  const removeSigner = (index) => {
    if (signers.length > 1) {
      // Don't allow removing signers who have already signed
      if (signers[index].status === 'signed') {
        toast.error('Cannot remove a signer who has already signed');
        return;
      }
      setSigners(signers.filter((_, i) => i !== index));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    const validSigners = signers.filter(s => s.name && s.email);
    if (validSigners.length === 0) {
      toast.error('Please add at least one signer with name and email');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/esignature/requests/${request.id}`, {
        name,
        message,
        signers: validSigners.map(s => ({
          id: s.id,
          type: s.type,
          name: s.name,
          email: s.email,
          role: s.role,
          order: s.order
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Signing request updated');
      onSuccess();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update signing request');
    } finally {
      setSaving(false);
    }
  };

  const isSent = ['sent', 'in_progress'].includes(request.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-auto py-8">
      <Card className="w-full max-w-2xl mx-4 p-6">
        <h2 className="text-xl font-semibold mb-4">
          Edit Signing Request
          {isSent && (
            <Badge variant="secondary" className="ml-2">Sent</Badge>
          )}
        </h2>
        
        {isSent && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            This request has already been sent. You can add new signers or update details for signers who haven't signed yet.
          </div>
        )}
        
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Request Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., John Smith Employment Agreement"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Message to Signers</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional message to include in the signing request email"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
            />
          </div>

          {/* Signers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Signers</label>
              <Button type="button" variant="outline" size="sm" onClick={addSigner}>
                <Plus className="w-4 h-4 mr-1" /> Add Signer
              </Button>
            </div>
            
            <div className="space-y-3">
              {signers.map((signer, index) => {
                const hasSigned = signer.status === 'signed';
                const signerStatus = SIGNER_STATUS_CONFIG[signer.status] || SIGNER_STATUS_CONFIG.pending;
                
                return (
                  <div key={signer.id || index} className={`p-3 border rounded-lg ${hasSigned ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Signer {index + 1}</span>
                        <span className={`text-xs ${signerStatus.color}`}>({signerStatus.label})</span>
                      </div>
                      {!hasSigned && signers.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeSigner(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <PersonnelNameAutocomplete
                        value={signer.name}
                        onChange={(v) => updateSigner(index, 'name', v)}
                        onSelect={(p) => {
                          const newSigners = [...signers];
                          newSigners[index] = {
                            ...newSigners[index],
                            name: p.name,
                            email: p.email || newSigners[index].email,
                          };
                          setSigners(newSigners);
                        }}
                        placeholder="Name *"
                        disabled={hasSigned}
                        required
                        testId={`edit-signer-name-${index}`}
                      />
                      <Input
                        type="email"
                        placeholder="Email *"
                        value={signer.email}
                        onChange={(e) => updateSigner(index, 'email', e.target.value)}
                        disabled={hasSigned}
                        required
                      />
                      <Input
                        placeholder="Role"
                        value={signer.role}
                        onChange={(e) => updateSigner(index, 'role', e.target.value)}
                        disabled={hasSigned}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
